import CustomButton from "@/_components/CustomButton"
import { loc } from "@/_services/localization"
import { addOops } from "@/_services/notification"
import { getUsername } from "@/_services/userConfiguration"
import axios from "axios"
import { lowerCaseFirstLetter, toQueryString } from "basikon-common-utils"
import JSON5 from "json5"
import React from "react"
import AceEditor from "react-ace"
import { ButtonGroup, Col, Modal, Row } from "react-bootstrap"

// WARNING: these imports must come after the AceEditor, otherwise webpack throws an error
import "ace-builds/src-noconflict/ext-searchbox"
import "ace-builds/src-noconflict/mode-json"
import "ace-builds/src-noconflict/theme-github"
import "ace-builds/webpack-resolver"

function convertToJs(entity) {
  const string = JSON5.stringify(entity, { space: 2, quote: '"' })

  // this block is a hack to group colProps in one line. Can format way better but this works for me
  try {
    const json = string.split("\n")
    const json2 = []
    for (let i = 0; i < json.length; i++) {
      let line = json[i]
      const tline = line.trim()
      if (tline === "colProps: {" || /props_.*: {/.test(tline)) {
        while (++i < json.length) {
          const line2 = json[i].trim()
          if (line2 === "}" || line2 === "},") {
            line = line.substring(0, line.length - 1) // remove trailing comma
            line += " " + line2.trim()
            break
          } else {
            line += " " + line2.trim()
          }
        }
      }
      json2.push(line)
    }
    return json2.join("\n")
  } catch (error) {
    console.error(error.stack)
  }

  return string
}

function convertToJson(entity) {
  return JSON.stringify(entity, null, 2)
}

export default class EntityModelModal extends React.Component {
  constructor(props) {
    super(props)

    this.state = {
      useJson: false,
    }
  }

  componentDidMount() {
    const { entity, showEntityFieldsButtons } = this.props

    // Select first model field button
    if (showEntityFieldsButtons) {
      const [firstKey] = Object.keys(entity).filter(it => entity[it])
      if (firstKey) this.setSelector(firstKey)
    }
  }

  checkScriptName = async scriptName => {
    if (!scriptName) return

    const isScriptNameTyped = scriptName.includes("Workflow-") || scriptName.includes("-layout-")
    if (isScriptNameTyped) {
      const untypedScriptName = scriptName.replace(/^(\w+Workflow)-\w+$/, "$1").replace(/^(\w+-layout)-\w+$/, "$1")

      let scripts = []
      try {
        const queryString = toQueryString({ name: [scriptName, untypedScriptName], projection: "-_id,name" })
        scripts = (await axios.get(`/api/script/scripts${queryString}`)).data
      } catch (error) {
        addOops(error)
      }

      const script = scripts.find(script => script.name === scriptName)
      if (!script) return untypedScriptName
    }

    return scriptName
  }

  openScript = async (scriptName, query = {}) => {
    if (scriptName) scriptName = await this.checkScriptName(scriptName)

    const queryString = toQueryString({ filter: scriptName, selectedSettingType: "script", name: scriptName, username: getUsername(), ...query })
    window.open(`/scripts${queryString}`)
  }

  setSelector = field => {
    if (this.state.selector === field) this.setState({ selector: undefined })
    else this.setState({ selector: field })
  }

  render() {
    const { entity, close, entityName, pageName, workflowName, showScriptButtons, showEntityFieldsButtons } = this.props
    const { /*useYml, */ useJson, selector } = this.state

    const entityType = entity?.type || entity?.[Object.keys(entity)[0]]?.type
    let firstButton
    if (pageName) firstButton = ["Page", () => this.openScript(pageName)]
    else {
      let layoutScriptName = lowerCaseFirstLetter(entityName) + "-layout"
      if (entityType) layoutScriptName += `-${entityType}`

      firstButton = ["Layout", () => this.openScript(layoutScriptName)]
    }

    const buttons = [
      firstButton,
      workflowName && ["Workflow", () => this.openScript(workflowName, { entityType })],
      ["Extension", () => this.openScript("collection-extension", { entityName })],
    ].filter(it => it)

    const displayEntity = selector ? entity[selector] : entity

    return (
      <Modal show={true} onHide={close} bsSize="large" className="entity-model-modal">
        <Modal.Header closeButton>
          <Modal.Title>{loc`Model`}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="mb-theme">
            <Col xs={12}>
              {showEntityFieldsButtons && (
                <ButtonGroup>
                  {Object.keys(entity)
                    .filter(it => entity[it])
                    .map(entityField => (
                      <CustomButton
                        fill={selector === entityField}
                        bsSize="xs"
                        bsStyle="default"
                        key={entityField}
                        onClick={() => this.setSelector(entityField)}
                      >
                        {entityField}
                      </CustomButton>
                    ))}
                </ButtonGroup>
              )}
              <CustomButton pullRight fill={useJson} bsSize="xsmall" bsStyle="default" onClick={() => this.setState({ useJson: !useJson })}>
                {loc`JSON`}
              </CustomButton>
              {showScriptButtons &&
                buttons.map(([title, onclick]) => (
                  <CustomButton key={title} pullRight bsSize="xsmall" bsStyle="default" className="flex-center" onClick={onclick}>
                    {loc(title)}
                    <i className="icn-external-link icn-xs ml-5px" />
                  </CustomButton>
                ))}
            </Col>
          </Row>

          <Row>
            <Col xs={12}>
              <AceEditor
                focus={true}
                readOnly
                mode="json"
                theme="github"
                value={useJson ? convertToJson(displayEntity) : convertToJs(displayEntity)}
                name="entity-model"
                showGutter={true}
                editorProps={{ $blockScrolling: true }}
                setOptions={{
                  copyWithEmptySelection: true,
                  useWorker: false, // disable syntax check as it does not support ESNext (async await etc.)
                }}
                width="100%"
                height="70rem"
                showPrintMargin={false}
              />
            </Col>
          </Row>
        </Modal.Body>

        <Modal.Footer>
          <CustomButton bsSize="sm" onClick={close}>
            {loc`Close`}
          </CustomButton>
        </Modal.Footer>
      </Modal>
    )
  }
}
